import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { SiteLayout } from "@/components/site/site-layout";

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Create account — CoderLity" },
      { name: "description", content: "Create your CoderLity account to get started." },
    ],
  }),
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session) throw redirect({ to: "/" });
  },
  component: SignupPage,
});

function SignupPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState<"form" | "verify" | "link_sent">("form");
  const [otpCode, setOtpCode] = useState("");
  const [method, setMethod] = useState<"otp" | "link" | "none">("otp");
  const [enabled, setEnabled] = useState(true);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "auth")
        .maybeSingle();
      const v = (data?.value ?? {}) as {
        enabled?: boolean;
        confirmation_method?: "otp" | "link" | "none";
        require_email_confirmation?: boolean;
      };
      setEnabled(v.enabled !== false);
      // back-compat: require_email_confirmation === false ⇒ no confirmation
      if (v.require_email_confirmation === false || v.enabled === false) setMethod("none");
      else setMethod(v.confirmation_method ?? "otp");
    })();
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/` },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    if (data.session) {
      toast.success("Account created!");
      navigate({ to: "/" });
      return;
    }
    if (!enabled || method === "none") {
      toast.success("Account created. You can now sign in.");
      navigate({ to: "/login" });
      return;
    }
    if (method === "link") {
      toast.success("Verification link sent. Check your inbox.");
      setStage("link_sent");
      return;
    }
    toast.success("We emailed you a 6-digit code.");
    setStage("verify");
    setResendCooldown(60);
  };

  const onVerify = async (e: FormEvent) => {
    e.preventDefault();
    setOtpError(null);
    if (!/^\d{6}$/.test(otpCode)) {
      setOtpError("Enter the 6-digit numeric code from your email.");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.verifyOtp({
      email,
      token: otpCode,
      type: "signup",
    });
    setLoading(false);
    if (error) {
      const msg = error.message?.toLowerCase() ?? "";
      let friendly = error.message;
      if (msg.includes("expired")) {
        friendly = "This code has expired. Tap “Resend code” to get a new one.";
      } else if (msg.includes("invalid") || msg.includes("token")) {
        friendly = "Incorrect code. Double-check the 6 digits and try again.";
      } else if (msg.includes("rate") || msg.includes("too many")) {
        friendly = "Too many attempts. Please wait a moment before trying again.";
      }
      setOtpError(friendly);
      toast.error(friendly);
      setOtpCode("");
      return;
    }
    toast.success("Email confirmed!");
    navigate({ to: "/" });
  };

  const resend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    const { error } = await supabase.auth.resend({ type: "signup", email });
    setResending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Code resent");
    setResendCooldown(60);
    setOtpError(null);
    setOtpCode("");
  };

  return (
    <SiteLayout>
      <div className="flex items-center justify-center bg-background px-4 py-16">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>
              {stage === "form" ? "Create account"
                : stage === "link_sent" ? "Check your email"
                : "Confirm your email"}
            </CardTitle>
            <CardDescription>
              {stage === "form" ? "Get started with CoderLity in seconds."
                : stage === "link_sent" ? `We sent a verification link to ${email}. Click it to activate your account.`
                : `Enter the 6-digit code sent to ${email}.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stage === "form" ? (
              <>
                <form onSubmit={onSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" type="password" autoComplete="new-password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Creating account..." : "Create account"}
                  </Button>
                  <p className="text-center text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <Link to="/login" className="font-medium text-primary hover:underline">Sign in</Link>
                  </p>
                </form>
              </>
            ) : stage === "link_sent" ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Didn't get the email? Check your spam folder, or resend it.
                </p>
                <div className="flex flex-col gap-2">
                  <Button variant="outline" onClick={resend}>Resend link</Button>
                  <button type="button" className="text-sm text-muted-foreground hover:underline" onClick={() => setStage("form")}>
                    Use a different email
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={onVerify} className="space-y-4">
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpCode}
                    onChange={(v) => {
                      setOtpCode(v.replace(/\D/g, ""));
                      if (otpError) setOtpError(null);
                    }}
                    aria-invalid={!!otpError}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {otpError && (
                  <p role="alert" className="text-center text-sm text-destructive">
                    {otpError}
                  </p>
                )}
                <Button type="submit" className="w-full" disabled={loading || otpCode.length !== 6}>
                  {loading ? "Verifying..." : "Verify & continue"}
                </Button>
                <div className="space-y-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={resend}
                    disabled={resending || resendCooldown > 0}
                  >
                    {resending
                      ? "Sending..."
                      : resendCooldown > 0
                        ? `Resend code in ${resendCooldown}s`
                        : "Resend code"}
                  </Button>
                  <div className="text-center text-sm">
                    <button type="button" className="text-muted-foreground hover:underline" onClick={() => setStage("form")}>
                      Use a different email
                    </button>
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </SiteLayout>
  );
}
